import React from 'react';
import _ from 'lodash';
import LivePage from '../LivePage';
import LegoAdminPageContext from '../legoAdminPageContext';
import BadgeId from '../../components/common/BadgeId';
import { IconButton } from '../../components/common/IconButton';
import submenuCerokm from '../menus/submenu-cerokm';
import LiveTable  from './components/LiveTable';
import SpecsGroupSelector from './spec-schema-editor/SpecsGroupSelector';
import ModelContextEditor from '../../components/lego/ModelContextEditor';
import SpecsSchemaChangePreview from './components/SpecsSchemaChangePreview';
import { SwitchInput } from '../../components/common/SwitchInput';
import { Icon } from '../../components/common/Icon';
import SpecFactoidImageEditor from './components/SpecFactoidImageEditor';
import { FragmentContent } from './components/FragmentContent';
import { FragmentFactoids } from './components/FragmentFactoids';
import AIModelSelector from './components/AIModelSelector';
import SpecSourcesList from './components/SpecSourcesList';
import { getSchemaRegex } from '../../../../model/cerokm/cerokmUtils.mjs';
import MongoDocEditor from '../../components/lego/MongoDocEditor';
import FragmentTypeEditor from './components/FragmentsTypeEditor';
import FragmentEditor from './components/FragmentEditor';
import MultipleItemsEditor from '../../components/lego/MultipleItemsEditor';
import SchemaPathInput from './spec-schema-editor/SchemaPathInput';
import JsonTextEditor from '../../components/common/editors/JsonTextEditor';
import SpecGroupBadge from './components/SpecGroupBadge';
import StackedBadgeList from './components/StackedBadgeList';


class CeroKmFragmentsManager extends LivePage {
  constructor(props) {
    super(props);
    this.svcSource = this.service('services/cerokm/source');
    this.svcFragments = this.service('services/cerokm/sourcefragment');
    this.svcFactoids = this.service('services/cerokm/sourcefactoid');
    this.svcSchemaFixes = this.service('services/cerokm/schemafix');
    this.svcAi = this.service('services/cerokm/ai/process-fragments');
    this.submenu = submenuCerokm;

    let filters = this.getUrlParam('filters', true);
    if(filters) {
      this.state.filters = filters;
      console.log("Loaded filters from URL", filters)
    } else {
      this.state.filters = {context: {modelId: 'Toyota-Tacoma'}};
    }

    this.tableRef = React.createRef();
  }

  filterByText(text) {
    this.updateFilters(text ? { ...this.state.filters, text: { $regex: text, $options: 'i' } } : _.omit(this.state.filters, 'text'));
  }

  filterByGroup(specsGroup) {
    let filters = { ...this.state.filters };

    if (specsGroup?.length === 1) {
      filters.specsGroupIds = specsGroup[0];
    } else if (specsGroup?.length > 1) {
      filters.specsGroupIds = {$in: specsGroup};
    } else {
      delete filters.specsGroupIds;
    }

    this.updateFilters(filters);
  }

  filterByNoFactoids(noFactoids) {
    this.updateFilters({ ... this.state.filters, noFactoids });
  }

  filterByNoSpecs(noSpecs) {
    this.updateFilters({ ... this.state.filters, noSpecs});
  }

  filterByFactoidText(text) {
    this.updateFilters(text ? { ...this.state.filters, factoidText: text } : _.omit(this.state.filters, 'factoidText'));
  }

  filterByFragmentId(id) {
    this.updateFilters(id ? { ...this.state.filters, _id: id } : _.omit(this.state.filters, '_id'));
  }

  filterBySchemaRegex(minVersion) {
    const regex = getSchemaRegex(minVersion);
    console.log("Regex", regex);
    this.updateFilters(regex ? { ...this.state.filters, schemaRegex: regex } : _.omit(this.state.filters, 'schemaRegex'));
  }

  filterByContext(context) {
    this.updateFilters(!_.isEmpty(context) ? { ...this.state.filters, context } : _.omit(this.state.filters, 'context'));
  }

  filterByType(type) {
    this.updateFilters(type ? { ...this.state.filters, type } : _.omit(this.state.filters, 'type'));
  }

  updateFilters(filters) {
    this.setState({ filters });
    if (_.isEmpty(filters)) {
      this.deleteUrlParam('filters');
    } else {
      console.log("Setting filters", filters);
      this.setUrlParam('filters', filters);
    }
  }

  acceptFactoids(factoids) {
    const ids = _.map(factoids, f => f._id);
    const sourceFragmentId = factoids[0].sourceFragmentId;

    this.runAsync((async () => {
      let sameFragmentWituseutManualRevision = {_id: {$nin: ids}, sourceFragmentId, manualRevision: {$exists: false}};

      await Promise.all([
        this.svcFactoids.update({query: { _id: {$in: ids} }}, {$set: {manualRevision: 'correct' }, $unset: {isObsolete: true, obsoleteReason: true}}),
        this.svcFactoids.update({query: sameFragmentWithoutManualRevision}, {$set: {manualRevision: 'incorrect', isObsolete: true, obsoleteReason: 'Revision' }}),
        ]);
      this.tableRef.current.refreshIds([sourceFragmentId], {$populate: true});
    })())
  }

  rejectFactoids(factoids) {
    const ids = _.map(factoids, f => f._id);
    const sourceFragmentId = factoids[0].sourceFragmentId;

    this.runAsync((async () => {
      await  this.svcFactoids.update({query: { _id: {$in: ids} }}, {$set: {manualRevision: 'incorrect', isObsolete: true, obsoleteReason: 'Revision'}}),
      this.tableRef.current.refreshIds([sourceFragmentId], {$populate: true});
    })())
  }

  deleteFactoids(fragmentIds) {
    let filter = {
      sourceFragmentId: { $in: fragmentIds },
    };
    this.runAsync(this.svcFactoids.remove({query: filter}).then(() => this.tableRef.current.refreshIds(fragmentIds)), 'Deleting factoids');
  }

  deleteFragments(objs) {
    const ids = _.map(objs, '_id');
    if(confirm(`Are you sure you want to DELETE  ${ids.length} FRAGMENTS?`)) {
      this.runAsync(async () => {
        await this.svcFactoids.remove({query: { sourceFragmentId: { $in: ids }, }})
        await this.svcFragments.remove({query: { _id: {$in: ids} }});
        this.tableRef.current.clearSelection();
        this.tableRef.current.refresh();
      }, `Deleting ${ids.length} fragments`);
    }
  }

  async toggleDisableFragments(objs, disable) {
    const ids = _.map(objs, '_id');
    const operation = disable ? { $set: { disabled: true } } : { $unset: { disabled: true } };

    if (disable) {
      let noteText = await this.prompt("Enter a note explaining why you are disabling the fragment");
      if (noteText) {
        operation.$push = {
          notes: {
            content: noteText,
            userId: this.getLoggedUserSignature().id,
            createdAt: new Date()
          }
        }
      }
    }

    await this.runAsync(async () => {
      await this.svcFragments.update({ query: { _id: { $in: ids } } }, operation);
      this.tableRef.current.refresh();
    }, `${disable ? 'Disabling' : 'Enabling'} ${ids.length} fragments`);

    // Update affected specs group's specs
    await this.runAsync(this.runServerScript('SaveSpecs', {specsGroupIds: this.getUniqueSpecsGroupIdsFromFragments(objs).join(',')}), 'Updating affected specs...');
  }

  getUniqueSpecsGroupIdsFromFragments(fragments) {
    return _.uniq(_.map(_.flatMap(fragments, f => f.specsGroupIds), specGroupId => specGroupId.toString()));
  }

  seeFixes(fragment) {
    this.runAsync(this.svcSchemaFixes.find({query: {_id: {$in: fragment.schemaFixes}}}).then((res) => {
      res = res[0];
      if(res) {
        this.openModal(<SpecsSchemaChangePreview changes={res.fixOperations} usage={res.fixUsage}/>, {title: 'Results'});
      } else {
        alert('No fixes found');
      }
    }));
  }

  openAddNewFactoid(factoidToClone, fragment) {
    let baseObj = {};

    if (factoidToClone) {
      let { sourceFragmentId, derivedSpecs, schemaVersion } = factoidToClone;
      baseObj = {
        sourceFragmentId,
        derivedSpecs,
        schemaVersion,
      }
    } else {
      baseObj = {
        sourceFragmentId: fragment._id,
        derivedSpecs: ['path', 'value'],
      };
    }
    // Add placeholder even when cloning for easier editing
    baseObj.state = 'complete';
    baseObj.manualRevision = 'correct';

    const extendSchema = {
      derivedSpecs: (value, onChange) => <MultipleItemsEditor  value={value} onChange={onChange} compact={true} buttonsAlwaysVisible={true} defaultNewItem={["path", "value"]}
       renderComponent={(item, {key, onChange}) => <div className='d-flex' key={key}>
          <SchemaPathInput value={item[0]} onChange={(path) => onChange([path, item[1]])} />
          <JsonTextEditor value={item[1]} onChange={(value) => onChange([item[0], value])} />
        </div>} />
    }


    this.openModal(<MongoDocEditor hideState={true} extendSchema={extendSchema} obj={baseObj} onCancel={() => this.closeModal()} onSave={async (obj) => {
      obj.derivedBy = this.getLoggedUserSignature().id;

      // Create fragment
      const res = await this.runAsync(async () => await this.svcFactoids.create(obj), "Creating factoid...");

      // Update UI so that fragment is visible
      this.tableRef.current.refreshIds([res[0]._id], {$populate: true});

      // Update affected specs group's specs
      await this.runAsync(this.runServerScript('SaveSpecs', {specsGroupIds: fragment.specsGroupIds.join(',')}), 'Updating affected specs...');
    }} />, { title: 'Add new factoid' });
  }

  openEditFragmentModal(fragment) {
    if(fragment.factoids) {
      fragment = _.omit(fragment, 'factoids');
    }
    this.openModal(<FragmentEditor value={fragment} onCancel={() => this.closeModal()} onSave={async (originalObj) => {
      let obj = _.cloneDeep(originalObj);
      obj.sourceIds = obj.sourceIds || [];
      if(obj.newSource?.sourceType) {
        obj.sourceIds.push(obj.newSource);
      }
      delete obj.newSource;

      if(obj.specsGroupIds?.length === 0) {
        return this.alert('All fragments must have some specs group. Edit "specsGroupIds" property.', 'Error');
      }
      if(obj.sourceIds?.length === 0) {
        return this.alert('All fragments must have some source. Edit "newSource" property.', 'Error');
      }
      if(!obj.notes?.length) {
        delete obj.notes;
      }

      // Create fragment
      let res;
      if(fragment?._id) {
        obj.updatedBy = this.getLoggedUserSignature();
        res = await this.runAsync(async () => await this.svcFragments.update(obj._id, obj), "Updating fragment...");
        res = [res];
      } else {
        obj.createdBy = this.getLoggedUserSignature();
        res = await this.runAsync(async () => await this.svcFragments.create([obj]), "Creating fragment...");
        this.showNotification({title: 'New fragment created', level: 'success',  body: <div>The id of the new fragment is:<br/><span className='text-primary'>{res[0]._id}</span></div>});
      }

      this.closeModal();
      // Update UI so that fragment is visible
      this.tableRef.current.refresh();

      if (obj.type === 'textHardSpec') {
        // Parse new fragments
        await this.runAsync(this.svcAi.create({ fragmentIds: _.map(res, '_id') }), `Parsing ${res.length} new fragment...`);

        // Update UI so that fragment is visible
        this.tableRef.current.refreshIds([res[0]._id], { $populate: true });

        // Update affected specs group's specs
        await this.runAsync(this.runServerScript('SaveSpecs', { specsGroupIds: obj.specsGroupIds.join(',') }), 'Updating affected specs...');
      }
    }} />, { title: fragment?._id ? 'Edit fragment' : 'Add new fragment' });
  }

  openAddNewFragmentModal(objectToClone = null) {
    let baseObj = {};

    if (objectToClone) {
      let { factoids, _id, schemaFixes, disabled, createdBy, createdAt, updatedAt, updatedBy, __v, ...rest } = objectToClone;
      baseObj = _.cloneDeep(rest);
      delete baseObj?.sourceContext?.createdBy;
    } else {
      const isImage = this.state.filters?.type === 'image';

      baseObj = {
        type: this.state.filters?.type || 'textHardSpec',
        text:  isImage ? '' : 'New fragment content',
        specsGroupIds: this.state.filters?.specsGroupIds?.$in || _.compact([this.state.filters?.specsGroupIds]),
        sourceIds: [],
      };

      if(isImage) {
        baseObj.sourceContext = {
          isRender: false,
          originalTrim: '',
          camera: ''
        }
      }
    }
    // Add placeholder even when cloning for easier editing
    baseObj.sourceContext = {
      ... baseObj.sourceContext
    };
    baseObj.newSource = {
      sourceType: '',
      sourceUrl: ''
    };
    baseObj.notes = [];

    this.openEditFragmentModal(baseObj);
  }

  renderPageBody() {
    const {filters, highlightedGroups} = this.state;

    let context = filters?.context;
    let hasGroupFilter = filters?.specsGroupIds;

    const fragmentsTableColumns = {
      Type: {
        sortBy: o => o.type,
        displayFunc: o => <span className={`badge badge-${o.type === 'image' ? 'success' : 'dark'}`}>{o.type}</span>
      },
      Text: {
        sortBy: o => o.text,
        displayFunc: o => {
          return <div className={ o.disabled ? 'translucent bg-light-danger' : '' }>
            {o.disabled ? <div>
              <span className={'badge badge-danger'}>disabled</span>
              <IconButton level={'success'} icon={'done'}
                          onClick={() => this.toggleDisableFragments([o], false)}>Enable</IconButton>
            </div> : null}
            <div className={'d-flex'}>

              <div className={'small overflow-hidden flex-grow-0'} style={{ width: 'calc(50% - 10px)', marginRight: '10px' }}>
                <FragmentContent fragment={o} />
              </div>

              <div className={'flex-grow-0 position-relative'} style={{ width: '50%' }}>
                {o.type === 'image' && !o.factoids?.length ? <div className={'col-6'}>
                  <IconButton level={'primary'} fill icon={'add_photo_alternate'} className='ml-3' onClick={() => processImage([o])}>
                    Process image
                  </IconButton>
                </div> : null}

                {o.type === 'image' && o.factoids?.length ?
                  <IconButton level={'primary'} icon={'edit'} className='ml-3 position-absolute' style={{ bottom: '5px', right: '5px' }} onClick={() => processImage([o])} />
                  : null}

                <FragmentFactoids
                  fragment={o}
                  onAcceptFactoids={(factoids) => this.acceptFactoids(factoids)}
                  onRejectFactoids={(factoids) => this.rejectFactoids(factoids)}
                  onCloneFactoid={(factoid) => this.openAddNewFactoid(factoid, o)}
                />
              </div>
            </div>
          </div>;
        }
      },

      fixes: o => o.schemaFixes?.length ? <span className={'btn btn-sm btn-success'}
        onClick={() => this.seeFixes(o)}>
        <Icon icon={'auto_fix_high'} /> See {o.schemaFixes.length} fix proposals
      </span> : null,

      "Specs groups": o => <StackedBadgeList
        // onMouseEnter={() => this.setState({ highlightedGroups: o.specsGroupIds })}
        // onMouseLeave={() => this.setState({ highlightedGroups: null })}
        >
        {o.specsGroupIds.map(id => <div key={id}>
          <SpecGroupBadge id={id} />
        </div>)}
      </StackedBadgeList>,

      sourceIds: o => <SpecSourcesList sourceIds={o.sourceIds} />
    };

    const processFragments = async (objs) => {
      await this.runAsync(this.svcAi.create({fragmentIds: _.map(objs, '_id'), model: this.state.model}).then((res) => {
        this.tableRef.current.refreshIds(_.map(objs, '_id'),{$populate: true});

        if (res.parseStats?.errors?.length > 0) {
          this.showNotification({
            title: `${res.parseStats.errors.length} errors parsing the ${objs.length} fragments`,
            body: <div>{res.parseStats.errors.map((e, i) => <div key={i}>
              <span className='text-secondary small'>"{e.fragmentText}"</span>: {e.error}
            </div>)}</div>,
            level: 'error'
          });
        }
      }));

      // Update affected specs group's specs
      await this.runAsync(this.runServerScript('SaveSpecs', {specsGroupIds: this.getUniqueSpecsGroupIdsFromFragments(objs).join(',')}), 'Updating affected specs...');
    }

    const fixFragments = async (objs) => {
      this.runAsync(this.svcAi.find({query: {fragmentIds: _.map(objs, '_id'), model: this.state.model}}).then((res) => {
        console.log(res);
        this.openModal(<SpecsSchemaChangePreview changes={res.fixOperations} usage={res.fixUsage}/>, {title: 'Results'});
      }));
    }

    const processImage = async (objs) => {
      if (objs.length === 1) {
        const [o] = objs;
        this.openModal(<SpecFactoidImageEditor fragment={o} onChange={() => this.tableRef.current.refreshIds([o._id], { $populate: true })} />,
        { title: 'Process spec image', fullScreen: true, noPadding: true });
      } else {
        alert('Please select only one image');
      }
    }

    const renderActions = (objs) => {
      let types = _.uniq(_.map(objs, 'type'));

      const factoidCount = _.sumBy(objs, o => o.factoids?.length ?? 0);
      const deleteBtn = factoidCount ? <IconButton level={'danger'} icon={'delete_sweep'} onClick={() => this.deleteFactoids(objs.map(o => o._id))}>
        Delete {factoidCount} factoids
      </IconButton> : null;

      const deleteFragmentsBtn = <IconButton level={'danger'} icon={'delete_forever'} onClick={() => this.deleteFragments(objs)}>
        Delete {objs.length} fragments
      </IconButton>;

      const shouldDisable = _.some(objs, o => !o.disabled);
      const disableFragmentsBtn = shouldDisable ?
        <IconButton level={'danger'} icon={'not_interested'}
                    onClick={() => this.toggleDisableFragments(objs, true)}>Disable {objs.length}</IconButton> :
        <IconButton level={'success'} icon={'done'}
                    onClick={() => this.toggleDisableFragments(objs, false)}>Enable {objs.length}</IconButton>;

      const cloneFragmentBtn = objs.length === 1 ? <IconButton icon={'content_copy'} level={'success'} className={'ml-2'} onClick={() => this.openAddNewFragmentModal(objs[0])}>Clone</IconButton> : null;

      const editFragmentBtn = objs.length === 1 && (objs[0].createdBy || objs[0].sourceContext?.createdBy) ? <IconButton icon={'edit'} level={'primary'} className={'ml-2'} onClick={() => this.openEditFragmentModal(objs[0])}>Edit</IconButton> : null;

      if(types.length === 1 && types[0] === 'textHardSpec') {
        return <span className={'ml-5 text-dark'}>
          <IconButton level={'success'} icon={'auto_mode'} onClick={() => processFragments(objs)}>Process specs</IconButton>

        <span className={'d-inline-flex'} style={{ width: '300px' }}>
          <IconButton level={'primary'} icon={'auto_fix_high'} onClick={() => fixFragments(objs)}>Fix schema</IconButton>
          <AIModelSelector value={this.state.model} onChange={(v) => this.setState({ model: v })} />
        </span>

        { deleteBtn }
        { deleteFragmentsBtn }
        { disableFragmentsBtn }
        { cloneFragmentBtn }
        { editFragmentBtn }
      </span>;
      } else if(types.length === 1 && types[0] === 'image') {
        return <span className={'ml-5'}>
          <IconButton level={'success'} icon={'add_photo_alternate'} onClick={() => processImage(objs)}>Process image</IconButton>

          { deleteBtn }
          { deleteFragmentsBtn }
          { disableFragmentsBtn }
          { cloneFragmentBtn }
          { editFragmentBtn }
        </span>
      } else {
        return <span className={'ml-5 badge badge-warning'}>Mixed types { deleteBtn } { deleteFragmentsBtn } { disableFragmentsBtn }</span>
      }
    }

    let fragmentFilter = _.omit(this.state.filters || {}, 'context');
    if (!_.isEmpty(fragmentFilter)) {
      fragmentFilter.$limit = 500;
    }
    fragmentFilter.$populate = true;
    // fragmentFilter.noFactoids = false;
    // fragmentFilter.noSpecs = false;

    const textFilter = this.state.filters?.text?.$regex;
    // throw new Error('test');
    return <div className={'px-3'}>
      {/*<h5>*/}
      {/*  Sources*/}

      {/*</h5>*/}
      {/*<LiveTable service={this.svcSource}/>*/}


      <div className={'mt-3 mb-2 d-flex align-items-center'}>
        <h5 className={'mr-3'}>
          Specs Groups

        </h5>

        <div style={{width: '350px'}}>
          <ModelContextEditor value={context} onChange={(context) => this.filterByContext(context)}/>
        </div>
      </div>

      <SpecsGroupSelector highlightedIds={highlightedGroups} multi={true}
      initialSelection={filters?.specsGroupIds?.$in || _.compact([filters?.specsGroupIds])} context={context}
        onGroupSelected={(group) => this.filterByGroup(group)} />

      <h5 className={'mt-3'}>
        Fragments
          <IconButton icon={'sync'} level={'primary'} className={'ml-2'} onClick={() => this.tableRef.current.refresh()}/>

          <IconButton icon={'add'} fill level={'success'} className={'ml-2'} onClick={() => this.openAddNewFragmentModal()}>Add new fragment</IconButton>
      </h5>

      <div className={'d-flex h6 align'} style={{gap: '20px'}}>
        <SwitchInput triState={true} value={fragmentFilter?.noFactoids} onChange={v => this.filterByNoFactoids(v)}>
          Without factoids?
        </SwitchInput>

        <SwitchInput triState={true} value={fragmentFilter?.noSpecs} onChange={v => this.filterByNoSpecs(v)}>
          Missing specs?
        </SwitchInput>

        <FragmentTypeEditor value={this.state.filters?.type} onChange={v => this.filterByType(v)} />

        <IconButton level={'primary'} icon={'filter_list'} onClick={() => {
          const text = prompt('Enter text to filter by factoid spec path:');
          if (text !== null) { // Check if prompt was cancelled
            this.filterByFactoidText(text || undefined); // Pass undefined if text is empty string
          }
        }}>
          Filter by spec...
        </IconButton>

        <IconButton level={'primary'} icon={'numbers'} onClick={() => {
          const id = prompt('Enter Fragment ID to filter by:');
          if (id !== null) { // Check if prompt was cancelled
            let ids = _.compact(id.split(/[\W]+/));
            this.filterByFragmentId(ids.length > 1 ? {$in: ids}: id);
          }
        }}>
          Filter by Fragment ID...
        </IconButton>

        <IconButton level={'primary'} icon={'pin'} onClick={() => {
          const id = prompt('Enter min schema version to filter (e.g. "666"):');
          if (id) { // Check if prompt was cancelled
            this.filterBySchemaRegex(id);
          }
        }}>
          Min schema version...
        </IconButton>

        {hasGroupFilter ? <span className={'badge badge-light no-wrap'}>
          specsGroupId=
          <span className={'text-ellipsis d-inline-block'} style={{ maxWidth: '300px' }}>
            {(filters.specsGroupIds?.$in || [filters.specsGroupIds]).map(id => <BadgeId key={id} id={id} />)}
          </span>
          ({(filters.specsGroupIds?.$in || [filters.specsGroupIds]).length})  <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterByGroup(null)} />
        </span> : null}

        {textFilter ? <span className={'badge badge-light'}>
           text=<span className={'badge badge-primary'}>{textFilter}</span>
           <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterByText(null)}/>
         </span> : null}

        {filters.factoidText ? <span className={'badge badge-light'}>
           factoid=<span className={'badge badge-primary'}>{filters.factoidText}</span>
           <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterByFactoidText(null)}/>
         </span> : null}

        {filters.type ? <span className={'badge badge-light'}>
          type=<span className={'badge badge-primary'}>{filters.type}</span>
          <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterByType(null)} />
        </span> : null}

        {filters._id ? <span className={'badge badge-light'}>
           fragmentId=<BadgeId id={JSON.stringify(filters._id)}/>
           <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterByFragmentId(null)}/>
         </span> : null}

         {filters.schemaRegex ? <span className={'badge badge-light'}>
           schemaRegex=<span className={'badge badge-dark'}>{filters.schemaRegex}</span>
           <IconButton icon={'clear'} size={'sm'} level={'danger'} onClick={() => this.filterBySchemaRegex(null)}/>
         </span> : null}
      </div>

      <LiveTable service={this.svcFragments} columns={fragmentsTableColumns} selectionActions={renderActions}
                 ref={this.tableRef} filter={fragmentFilter} limit={fragmentFilter?.$limit} onTextSearch={(text) => this.filterByText(text)}/>
    </div>;
  }
}

CeroKmFragmentsManager.contextType = LegoAdminPageContext;



// For development
function FuncComponent(props) {
  return <CeroKmFragmentsManager {...props}/>;
}

export default FuncComponent;
